import { UserMessage } from '@bt-common/schemas/bt/user-message';
import ObjectId from 'bson-objectid';
import { TeamInboxActions } from './redux/TeamInboxReducer';
import { TeamInboxMessagesCacheReducerActions, TeamInboxChatsCacheReducerActions } from '@common-reducers/DBServiceThunks';
import { selectCurrentChatId, selectReplyingToMessage } from './redux/TeamInboxSelectors';
import ReplyPreviewBar from './reply-preview-bar';
import { getMessageShortId } from './team-inbox-helpers';
import { triggerRequesthWhatsappTool } from '@common/tools/whatsapp';
import { WA_TOOL_COMMANDS } from '@/bt-common/interfaces/whatsapp-tools-commands.interface';
import { selectUserId } from '@common/reducers/UserSelectors';
import AttachmentPreview from '@common-components/attachment-preview/attachment-preview';
import { useGetSignedUploadUrlMutation } from '@common-reducers/backend-api/backend-api';
import { uploadFilePut } from '@common-services/utils';
import RowStack from '@common/components/common/row-stack/RowStack';
import { useAppDispatch, useAppSelector } from '@common/reducers/hooks/store.hook';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { Box, CircularProgress, IconButton, Paper, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import TextWithEmojiInput from './text-with-emoji-input';
import { TeamInboxScheduleButton } from './team-inbox-schedule-button';

interface SendButtonProps {
    onClick: () => void;
    isLoading: boolean;
    buttonSize?: string;
    disabled?: boolean;
    context: 'message' | 'caption';
}

const SendButton: React.FC<SendButtonProps> = ({
    onClick,
    isLoading,
    context,
    disabled = false
}) => {
    const buttonSize = context === 'message' ? '40px' : '48px';
    // WhatsApp Web: the send control is a plain neutral icon until there's
    // something to send, then it becomes a filled green circle with a white
    // icon. `disabled` here means "nothing to send", so active === !disabled.
    const active = !disabled;

    return (
        <IconButton
            onClick={onClick}
            disabled={disabled}
            sx={{
                backgroundColor: active ? 'var(--wa-accent)' : 'transparent',
                color: active ? '#ffffff' : 'var(--wa-secondary-text)',
                '&:hover': {
                    backgroundColor: active ? 'var(--wa-accent)' : 'var(--wa-hover-action-bg)',
                },
                '&.Mui-disabled': {
                    backgroundColor: 'transparent',
                    color: 'var(--wa-secondary-text)',
                },
                width: buttonSize,
                height: buttonSize,
                transition: 'background-color 0.15s ease',
            }}
        >
            {isLoading ? (
                <CircularProgress size={24} color="inherit" />
            ) : (
                <SendIcon fontSize={context === 'message' ? 'small' : 'medium'} />
            )}
        </IconButton>
    );
};

interface MessageInputBarProps {
    droppedFiles?: File[];
    onClearDroppedFiles?: () => void;
}

interface Asset {
    url: string;
    name: string;
    type: string;
}

const MessageInputBar: React.FC<MessageInputBarProps> = React.memo(({ droppedFiles, onClearDroppedFiles }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const chatId = useAppSelector(selectCurrentChatId);
    const userId = useAppSelector(selectUserId);
    const replyingToMessage = useAppSelector(selectReplyingToMessage);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const composerRef = useRef<HTMLDivElement>(null);
    const [value, setValue] = useState('');
    const [asset, setAsset] = useState<Asset | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [getSignedUploadUrl] = useGetSignedUploadUrlMutation();

    const handleSendMessage = async () => {
        if ((!value.trim() && !asset) || !chatId || isLoading) return;

        setIsLoading(true);
        try {
            // Create a minimal message object for direct sending (no database record)
            // rev 3.3 §6.6: a real 24-hex ObjectId (the messageSecret token);
            // the old `team_inbox_send_<ms>_<rand>` (36 bytes) did not fit the
            // marked 32-byte secret and could never be matched back.
            const messageId = ObjectId().toHexString();
            // Reply target: the engine resolves quotedMessageId (a serialized
            // waMessageKey) via getWaMessageByWaMessageKey and sends as a quote
            // (WhatsappSendMessageThunk reads userMessage.quotedMessageId).
            const quotedMessageId: string | undefined = replyingToMessage?.waMessageKey?._serialized;
            const messageDraft: Partial<UserMessage> = {
                _id: messageId,
                id: messageId,
                owner: userId,
                message: value,
                contactList: [{ id: chatId, name: chatId }],
                asset: asset || undefined,
                quotedMessageId,
            };

            // Send directly via MQTT without creating a database record
            const result: any = await triggerRequesthWhatsappTool(WA_TOOL_COMMANDS.sendMessageThunkTool, {
                userMessage: messageDraft as UserMessage,
                isSendMessageNow: true,
                // rev 3.3 D9: a Team Inbox reply is a HUMAN intervention — the
                // backend records it (author 'human') so the agents' auto-takeover
                // sees it; stripped before the engine dispatch.
                author: 'human',
            });

            // Optimistic sync: surface the sent message immediately in the
            // open chat + chat-list preview from the tool's response (the
            // live listener event will reconcile/duplicate-upsert by key).
            // Wire shape (verified via /actions response): the sent-message
            // payload sits under data.response and waMessageKey is a full
            // KEY OBJECT — reading data.waMessageKey as a string silently
            // yielded undefined and skipped the optimistic upsert entirely.
            const sent = result?.success === false
                ? null
                : (result?.data?.response ?? result?.data ?? result);
            const sentKeyObj = (sent?.waMessageKey && typeof sent.waMessageKey === 'object')
                ? sent.waMessageKey
                : undefined;
            const waKey: string | undefined =
                sentKeyObj?._serialized
                ?? (typeof sent?.waMessageKey === 'string' ? sent.waMessageKey : undefined)
                ?? sent?.messageId;
            if (waKey) {
                const ts = sent?.t ?? Date.now() / 1000;
                const optimisticMessage = {
                    _id: waKey,
                    id: { _serialized: waKey },
                    waMessageKey: sentKeyObj ?? { _serialized: waKey, fromMe: true },
                    chatId,
                    waMessageId: waKey,
                    body: sent?.body ?? value,
                    caption: sent?.caption ?? null,
                    type: sent?.type ?? (asset ? 'image' : 'chat'),
                    fromMe: true,
                    t: ts,
                    timestamp: ts,
                    // The send already succeeded, so the message is at least
                    // "sent" (ack 1, single check) — never show the pending
                    // clock (ack 0). Real delivered/read ticks arrive via the
                    // fromMe-gated receivedMessageAckUpdate event.
                    ack: typeof sent?.ack === 'number' ? sent.ack : 1,
                    hasAttachedMedia: Boolean(sent?.hasAttachedMedia),
                    filename: sent?.filename ?? null,
                    // Quote reference so the optimistic bubble instantly renders
                    // the reply block (same shape the engine attaches on fetch).
                    quotedMessage: replyingToMessage ? {
                        waMessageKey: replyingToMessage.waMessageKey,
                        text: (replyingToMessage.body || replyingToMessage.caption || '') || undefined,
                        type: replyingToMessage.type,
                        author: replyingToMessage.author?._serialized ?? replyingToMessage.author ?? null,
                    } : undefined,
                };
                // Dedupe vs the live own-message event, which can land FIRST
                // and under a different key serialization (participant/@lid
                // variants). Matching an existing same-chat entity by the
                // serialization-stable short stanza id and merging into it
                // prevents the message from briefly rendering twice.
                dispatch(((d: any, getState: any) => {
                    const entities = getState()?.DBTeamInboxMessagesCacheReducer?.entities || {};
                    const shortId = sentKeyObj?.id || waKey.split('_')[2];
                    const existing: any = shortId
                        ? Object.values(entities).find((m: any) =>
                            m?.chatId === chatId && getMessageShortId(m) === shortId)
                        : undefined;
                    if (existing && existing._id !== waKey) {
                        const { _id, id, waMessageKey, ...mergeFields } = optimisticMessage as any;
                        d(TeamInboxMessagesCacheReducerActions.updateOne({
                            id: existing._id,
                            changes: Object.fromEntries(
                                Object.entries(mergeFields).filter(([, v]) => v !== undefined),
                            ),
                        }));
                    } else {
                        d(TeamInboxMessagesCacheReducerActions.upsertMany([optimisticMessage]));
                    }
                }) as any);
                dispatch(TeamInboxChatsCacheReducerActions.updateOne({
                    id: chatId,
                    changes: { lastMessage: optimisticMessage, timestamp: ts },
                }));
            }

            setValue('');
            handleRemoveAttachment();
            // Reply is consumed by this send — clear the staged target.
            if (replyingToMessage) {
                dispatch(TeamInboxActions.setReplyingToMessage(null));
            }
        } catch (error) {
            console.error('[team-inbox] Send message failed', { chatId, error });
        } finally {
            setIsLoading(false);
        }
    };

    const handleTextWithEmojiKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            if (replyingToMessage) {
                // First Escape cancels the staged reply (WA behavior).
                dispatch(TeamInboxActions.setReplyingToMessage(null));
            } else if (asset) {
                handleRemoveAttachment();
            } else {
                setValue('');
                dispatch(TeamInboxActions.resetChatViewMode());
            }
        }
    };

    const handleFileChange = useCallback(async (files: File[]) => {
        if (!files || files.length === 0 || !chatId) return;

        setIsLoading(true);
        const file = files[0];
        try {
            const uploadUrlResponse = await getSignedUploadUrl(file.type).unwrap();
            await uploadFilePut(file, uploadUrlResponse.url);

            const newAsset: Asset = {
                url: uploadUrlResponse.publicUrl,
                name: file.name,
                type: file.type,
            };

            setAsset(newAsset);
        } catch (error) {
            console.error('Failed to upload file:', error);
        } finally {
            setIsLoading(false);
        }
    }, [chatId, getSignedUploadUrl]);

    const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.target.files && e.target.files.length > 0) {
            handleFileChange(Array.from(e.target.files));
        }
    };

    const handleAttachButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveAttachment = useCallback(() => {
        setAsset(null);
        // Call the callback to clear dropped files in parent component
        if (onClearDroppedFiles) {
            onClearDroppedFiles();
        }
    }, [onClearDroppedFiles]);

    // Dropzone setup for the message input area
    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (!chatId) return;
        handleFileChange(acceptedFiles);
    }, [chatId, handleFileChange]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        noClick: true,
        disabled: !chatId || isLoading
    });

    // Reset message input when chatId changes
    useEffect(() => {
        setValue('');
        handleRemoveAttachment();
    }, [chatId, handleRemoveAttachment]);

    // Process dropped files from parent component
    useEffect(() => {
        if (droppedFiles && droppedFiles.length > 0) {
            handleFileChange(droppedFiles);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [droppedFiles]);

    // Focus the composer when a reply is staged (WA jumps focus to the input).
    // Deferred a tick: the context menu / popover that staged the reply is
    // still unmounting, and its focus handling would otherwise race ours.
    useEffect(() => {
        if (!replyingToMessage) return;
        const focusInput = () =>
            composerRef.current?.querySelector<HTMLElement>('input, textarea')?.focus();
        focusInput();
        const timer = setTimeout(focusInput, 120);
        return () => clearTimeout(timer);
    }, [replyingToMessage]);

    return (
        <>
            {asset && (
                // Full screen attachment preview layout
                <Box sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'var(--color-surface)',
                }}>
                    {/* Header row: filename and close button */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        borderBottom: `1px solid var(--color-border)`,
                        flexShrink: 0,
                    }}>
                        <IconButton
                            onClick={handleRemoveAttachment}
                            sx={{ color: 'var(--color-text-muted)' }}
                        >
                            <CloseIcon />
                        </IconButton>

                        <Box sx={{
                            fontWeight: 500,
                            fontSize: '1rem',
                            color: 'var(--color-text)',
                            textAlign: 'center',
                            flex: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            px: 2
                        }}>
                            {asset.name}
                        </Box>

                        <Box sx={{ width: 40 }} /> {/* Empty space to balance the close button */}
                    </Box>

                    {/* File preview area */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'auto',
                        flexGrow: 1,
                        flexShrink: 1,
                        flexBasis: 0,
                        padding: '16px',
                    }}>
                        <Box sx={{
                            alignContent: 'center',
                            justifyContent: 'center',
                            height: '100%',
                        }}>
                            <AttachmentPreview
                                attachmentName={asset.name}
                                attachmentType={asset.type}
                                attachmentUrl={asset.url}
                                pdfPreviewHeight="100%"
                                previewMaxHeight="100%"
                                imageObjectFit="contain"
                            />
                        </Box>
                    </Box>

                    {/* Message input area */}
                    <ReplyPreviewBar />
                    <Box sx={{
                        padding: '16px',
                        flexShrink: 0,
                    }}>
                        <TextWithEmojiInput
                            value={value}
                            onChange={setValue}
                            onKeyDown={handleTextWithEmojiKeyDown}
                            onPasteFiles={handleFileChange}
                            isLoading={isLoading}
                            context="caption"
                        />

                        {/* Send button row */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <SendButton
                                onClick={handleSendMessage}
                                isLoading={isLoading}
                                context='caption'
                                disabled={(!value.trim() && !asset) || isLoading}
                            />
                        </Box>
                    </Box>
                </Box>
            )}

            {!asset && (
                // Regular message input
                <Box {...getRootProps()} sx={{ position: 'relative' }}>
                    <input {...getInputProps()} />

                    {isDragActive && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                zIndex: 20,
                                backgroundColor: 'var(--color-surface)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '8px',
                                border: `2px dashed var(--color-primary)`,
                                pointerEvents: 'none',
                            }}
                        >
                            <Box sx={{
                                color: 'var(--color-primary)',
                                fontWeight: 500,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}>
                                <AttachFileIcon sx={{ fontSize: 24, mb: 1 }} />
                                {t('teamInbox.dropFileHere')}
                            </Box>
                        </Box>
                    )}

                    {/* Reply preview bar (WA: rounded quote panel above the composer) */}
                    <ReplyPreviewBar />

                    <Paper
                        ref={composerRef}
                        elevation={0}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            // WhatsApp Web composer bar: flat panel background,
                            // tight vertical padding, no top divider.
                            padding: '5px 16px',
                            backgroundColor: 'var(--wa-panel-bg)',
                            borderTop: 'none',
                            borderRadius: 0,
                            position: 'relative',
                            zIndex: 10,
                        }}
                    >
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={onFileInputChange}
                        />

                        <RowStack sx={{ width: '100%', gap: 1, alignItems: 'center' }}>
                            <Tooltip title={t('teamInbox.attachFile')}>
                                <IconButton
                                    size="medium"
                                    onClick={handleAttachButtonClick}
                                    disabled={isLoading}
                                    sx={{
                                        color: 'var(--wa-secondary-text)',
                                        '&:hover': { backgroundColor: 'var(--wa-hover-action-bg)' }
                                    }}
                                >
                                    <AttachFileIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            <TextWithEmojiInput
                                value={value}
                                onChange={setValue}
                                onKeyDown={handleTextWithEmojiKeyDown}
                                onPasteFiles={handleFileChange}
                                isLoading={isLoading}
                                context="message"
                            />

                            {/* AI Assistant Button */}
                            <Tooltip title={t('teamInbox.aiAssistantComingSoon')}>
                                <IconButton
                                    size="medium"
                                    disabled={isLoading}
                                    sx={{
                                        color: 'var(--wa-secondary-text)',
                                        '&:hover': { backgroundColor: 'var(--wa-hover-action-bg)' }
                                    }}
                                    onClick={() => {
                                        console.log('AI Assistant clicked - feature coming soon for Team Inbox');
                                    }}
                                >
                                    <AutoAwesomeIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            {/* Schedule Message Button */}
                            <TeamInboxScheduleButton />

                            <SendButton
                                onClick={handleSendMessage}
                                isLoading={isLoading}
                                context='message'
                                disabled={(!value.trim() && !asset) || isLoading}
                            />
                        </RowStack>
                    </Paper>
                </Box>
            )}
        </>
    );
});

export default MessageInputBar;
